<template>
  <div
    v-if="synchronizationProgress?.completed === false"
    class="tw:flex tw:gap-2 tw:bg-blue-500 tw:p-3 tw:items-center tw:justify-center"
  >
    <FontAwesomeIcon class="tw:text-white tw:animate-spin" icon="fa-light fa-loader" />
    <p class="tw:body-2 tw:text-white">
      {{ trans._integrationV2.integrationStatus.replace("{percentage}", averageProgress.toString()) }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { ref, onUnmounted, computed } from "vue";
import { DTOIntegrationSynchronizationProgressResponse } from "vue-app/api/dto";
import { getIntegrationSynchronizationProgress } from "vue-app/api/integration/synchronization.ts";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";

const modelValue = defineModel<boolean>();

const synchronizationProgress = ref<DTOIntegrationSynchronizationProgressResponse | null>(null);
const wasIntegratedAtStart = window.EDRONE_CONTEXT.isIntegrationCompleted;
const trackerId = window.EDRONE_CONTEXT?.trackerId;
const integrationCompleteKey = `onboarding_integration_complete_${trackerId}`;
let intervalId: ReturnType<typeof setInterval> | undefined;

const averageProgress = computed(() => {
  if (
    !synchronizationProgress.value ||
    !synchronizationProgress.value.types ||
    synchronizationProgress.value.types.length === 0
  ) {
    return 0;
  }
  const total = synchronizationProgress.value.types.reduce((acc, type) => acc + type.percentage, 0);
  return Math.round(total / synchronizationProgress.value.types.length);
});

function isEmptyObject(obj: object): boolean {
  return Object.keys(obj).length === 0;
}

async function getProgress() {
  const response = await getIntegrationSynchronizationProgress();
  if (response && !isEmptyObject(response)) {
    return response;
  }
  return null;
}

function sendIntegrationCompleteEvent() {
  if (localStorage.getItem(integrationCompleteKey)) {
    return;
  }

  const trackerCreationDate = new Date(window.EDRONE_CONTEXT.tracker.creationDate);
  const cutoffDate = new Date("2025-01-13T00:00:00");

  if (trackerCreationDate < cutoffDate) {
    localStorage.setItem(integrationCompleteKey, "true");
    return;
  }

  amplitudeSendEvent("Onboarding_Integration_Complete", {
    platform: window.EDRONE_CONTEXT?.tracker?.platform,
    trackerId,
  });
  localStorage.setItem(integrationCompleteKey, "true");
}

async function init() {
  if (wasIntegratedAtStart) {
    sendIntegrationCompleteEvent();
    return;
  }

  synchronizationProgress.value = await getProgress();

  if (synchronizationProgress.value?.completed) {
    sendIntegrationCompleteEvent();
    return;
  }

  intervalId = setInterval(
    async () => {
      synchronizationProgress.value = await getProgress();
      modelValue.value = synchronizationProgress.value != null && !synchronizationProgress.value.completed;
      if (synchronizationProgress.value?.completed) {
        sendIntegrationCompleteEvent();
        clearInterval(intervalId);
      }
    },
    synchronizationProgress.value?.completed === false ? 2500 : 10000
  );
}

init();

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>
